import { ofType } from 'redux-observable';
import { Observable, from, of } from 'rxjs';
import { auditTime, catchError, mergeMap, withLatestFrom } from 'rxjs/operators';
import { appError } from '@bridebook/web/lib/app/actions';
import { updateUserBudget } from '@bridebook/web/lib/budget/actions';
import { Action, IEpicDeps } from '@bridebook/web/lib/types';
import { upsertWeddingSupplier } from '../utils';
import { verifySpamCheck, blockAccount } from '@bridebook/web/lib/enquiries/utils/spam-check';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';

interface IBookingEpicCreatorArgs<A extends Action> {
  actionType: A['type'];
  analyticsType: string;
  booked: () => boolean;
  time?: number;
  auditTime?: number;
}

export const bookingEpicCreator =
  <A extends Action>({ actionType, analyticsType, booked, time = 0 }: IBookingEpicCreatorArgs<A>) =>
  (action$: Observable<A>, { state$ }: IEpicDeps) =>
    action$.pipe(
      ofType(actionType),
      auditTime(time),
      withLatestFrom(state$),
      mergeMap(
        ([
          {
            payload: { item, method, section },
          },
          state,
        ]) => {
          const weddingId = getWeddingId(state);

          const getPromise = async () => {
            if (!verifySpamCheck(state)) {
              await blockAccount(state);
              return false;
            }

            await upsertWeddingSupplier({
              supplier: item,
              weddingId,
              overwrite: { booked: booked() },
            });
            return true;
          };

          return from(getPromise()).pipe(
            mergeMap((success) =>
              success
                ? of(updateUserBudget(item), {
                    type: analyticsType,
                    payload: { item, method, section },
                  })
                : of(),
            ),
          );
        },
      ),
      catchError((error) => of(appError({ error, feature: 'Shortlist' }))),
    );
